import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { IUserSettings } from 'src/app/core/models/i-user-settings';
import { ISaveUserSettingsResponse } from 'src/app/core/models/i-save-user-settings-response';
import { ISaveDashboardSettingsRequest } from 'src/app/core/models/i-save-dashboard-settings-request';
import { UserRolesService } from './user-roles.service';
import { SaveUserSettingsRequest } from 'src/app/core/models/i-save-user-settings-request';
import { map, switchMap, take } from 'rxjs/operators';
import { USER_ROLES } from 'src/app/core/constants/cms-user-roles';
import { RestService } from './rest-service';
import { CitywireInternalAccountsService } from '@tpapa/citywire-internal-accounts-angular';

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  static readonly ENTITY_IDENTIFIER: string = 'user-settings';
  private readonly BASE_USER_SETTINGS_ROUTE = `api/${UserSettingsService.ENTITY_IDENTIFIER}`;
  private readonly BUILD_HTTP_ROUTE = (...args) =>
    this.restService.BUILD_HTTP_ROUTE(this.BASE_USER_SETTINGS_ROUTE, ...args);

  constructor(
    private restService: RestService,
    private accountService: CitywireInternalAccountsService,
    private userRolesService: UserRolesService
  ) {}

  getByUserName$(userName: string): Observable<IUserSettings> {
    const params = this.restService.buildHttpParamsObject({
      userName,
    });
    return this.restService.get$<IUserSettings>(this.BUILD_HTTP_ROUTE(), {
      params,
    });
  }

  getForCurrentUser$(): Observable<IUserSettings> {
    return this.getCurrentUserName$().pipe(
      switchMap((currentUser) => this.getByUserName$(currentUser))
    );
  }

  async save(userSettings: IUserSettings): Promise<ISaveUserSettingsResponse> {
    const currentUser = await this.getCurrentUserName$().toPromise();

    const request = new SaveUserSettingsRequest(
      userSettings,
      userSettings.user
    );

    if (currentUser !== userSettings.user) {
      const isAdmin = await this.userRolesService
        .isUserInRole('Admin')
        .toPromise();

      if (!isAdmin) {
        return throwError(
          `Cannot edit another user's settings if user is not an admin`
        ).toPromise();
      } else {
        return this.restService
          .post$<ISaveUserSettingsResponse>(this.BUILD_HTTP_ROUTE(), request)
          .toPromise();
      }
    } else {
      return this.restService
        .post$<ISaveUserSettingsResponse>(this.BUILD_HTTP_ROUTE(), request)
        .toPromise();
    }
  }

  update$(userSettings: IUserSettings): Observable<ISaveUserSettingsResponse> {
    const isOperationAllowed$ = this.getCurrentUserName$().pipe(
      switchMap((currentUser) => {
        return currentUser !== userSettings.user
          ? this.userRolesService.isUserInRole(USER_ROLES.Admin)
          : of(true);
      })
    );

    const request = new SaveUserSettingsRequest(
      userSettings,
      userSettings.user
    );

    return isOperationAllowed$.pipe(
      switchMap((isAllowed: boolean) => {
        return isAllowed
          ? this.restService.post$<ISaveUserSettingsResponse>(
              this.BUILD_HTTP_ROUTE(),
              request
            )
          : throwError(
              `Cannot edit another user's settings if user is not an admin`
            );
      })
    );
  }

  getCurrentUserName$(): Observable<string> {
    return this.accountService.currentUser.pipe(
      take(1),
      map((user) => user.username.toLowerCase())
    );
  }

  // TODO IMPROVEMENT: This should go to dashboardSettingsService instead or could just hit the regular update
  updateDashboardSettings$(
    request: ISaveDashboardSettingsRequest
  ): Observable<void> {
    return this.getCurrentUserName$().pipe(
      switchMap((currentUser) => {
        request.userName = currentUser;

        return this.restService.post$<void>(
          this.BUILD_HTTP_ROUTE('dashboard-settings'),
          request
        );
      })
    );
  }
}
